import { useEffect, useState } from 'react';
import { Authenticator } from '@aws-amplify/ui-react';
import { Hub } from 'aws-amplify/utils';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Alert, Box, Button, Stack, Typography, useTheme } from '@mui/material';
import '@aws-amplify/ui-react/styles.css';

import logoBlack from '../../assets/images/altvia-logo-black.svg';
import logoWhite from '../../assets/images/altvia-logo-white.svg';
import {
	AUTH_CHANNEL_NAME,
	SSO_POPUP_FLAG,
	createAuthLoader,
	useAuthConfig,
} from './authUtils';

export const loader = createAuthLoader;

const STYLES = {
	centered: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		height: '100vh',
		width: '100vw',
	},
	noAccountContainer: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'center',
		gap: '30px',
		height: '100vh',
		paddingBottom: '30vh',
		width: 300,
		alignSelf: 'center'
	},
};

const getProviderConfig = (provider) => {
	switch (provider) {
		case 'MicrosoftEntra':
		case 'Microsoft':
			return {
				displayName: 'Microsoft',
				icon: () => (
					<svg
						width='20'
						height='20'
						viewBox='0 0 30 30'
						style={{ fill: '#FFFFFF' }}
					>
						<path d='M 6 4 C 4.895 4 4 4.895 4 6 L 4 12 C 4 13.105 4.895 14 6 14 L 12 14 C 13.105 14 14 13.105 14 12 L 14 6 C 14 4.895 13.105 4 12 4 L 6 4 z M 18 4 C 16.895 4 16 4.895 16 6 L 16 12 C 16 13.105 16.895 14 18 14 L 24 14 C 25.105 14 26 13.105 26 12 L 26 6 C 26 4.895 25.105 4 24 4 L 18 4 z M 6 16 C 4.895 16 4 16.895 4 18 L 4 24 C 4 25.105 4.895 26 6 26 L 12 26 C 13.105 26 14 25.105 14 24 L 14 18 C 14 16.895 13.105 16 12 16 L 6 16 z M 18 16 C 16.895 16 16 16.895 16 18 L 16 24 C 16 25.105 16.895 26 18 26 L 24 26 C 25.105 26 26 25.105 26 24 L 26 18 C 26 16.895 25.105 16 24 16 L 18 16 z' />
					</svg>
				),
			};
		case 'Google':
			return {
				displayName: 'Google',
				icon: () => (
					<svg
						width='20'
						height='20'
						viewBox='0 0 24 24'
						style={{ fill: '#FFFFFF' }}
					>
						<path d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z' />
						<path d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z' />
						<path d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z' />
						<path d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z' />
					</svg>
				),
			};
		default:
			return null;
	}
};

const TokenReporter = () => {
	useEffect(() => {
		const reportToken = async () => {
			const authSession = await fetchAuthSession();
			const idToken = authSession.tokens.idToken.toString();

			if (window.opener && !window.opener.closed) {
				window.opener.postMessage(idToken, '*');
			} else {
				console.warn('No opener found. Refresh page to complete login.');
			}

			window.close();
		};

		reportToken();
	}, []);

	return <>Returning to SalesForce. This window will close automatically</>;
};

const NoAccountView = ({ logo }) => (
	<Box sx={STYLES.noAccountContainer}>
		<img src={logo} alt='Altvia Logo' />
		<Typography variant='body2'>
			User doesn&apos;t exist. Please reach out to us to create your account.
		</Typography>
		<Button
			variant='contained'
			color='primary'
			href='https://altvia.com/aime'
			target='_blank'
			rel='noopener noreferrer'
			onClick={() => window.close()}
		>
			Learn More
		</Button>
	</Box>
);

const SSOSignInHeader = ({ providers, onLogin, error, onClearError }) =>
	!!providers.length && (
		<Box textAlign='center' px={4} pt={4}>
			<Typography variant='h5' gutterBottom>
				Sign in to your account
			</Typography>
			<Stack spacing={1.5}>
				{providers.map((provider) => {
					const config = getProviderConfig(provider);
					if (!config) return null;

					const IconComponent = config.icon;
					return (
						<Button
							key={provider}
							variant='contained'
							color='primary'
							onClick={() => onLogin(provider)}
							fullWidth
							startIcon={<IconComponent />}
						>
							Sign in with {config.displayName}
						</Button>
					);
				})}
			</Stack>
			{error && (
				<Alert
					severity='error'
					onClose={onClearError}
					sx={{ width: '100%', mt: 2 }}
				>
					{error}
				</Alert>
			)}
		</Box>
	);

const usePopupSSOCallback = () => {
	useEffect(() => {
		const isPopupFlow = sessionStorage.getItem(SSO_POPUP_FLAG);
		if (!isPopupFlow) return;

		const broadcast = (message) => {
			const channel = new BroadcastChannel(AUTH_CHANNEL_NAME);
			channel.postMessage(message);
			channel.close();
		};

		const unsubscribe = Hub.listen('auth', async ({ payload }) => {
			if (payload.event === 'signInWithRedirect') {
				try {
					const session = await fetchAuthSession();
					const idToken = session.tokens.idToken.toString();
					broadcast({ type: 'SSO_SUCCESS', token: idToken });
				} catch (err) {
					console.error('Failed to get auth session:', err);
					broadcast({ type: 'SSO_ERROR', error: 'Failed to get session' });
				}
				sessionStorage.removeItem(SSO_POPUP_FLAG);
				window.close();
			} else if (payload.event === 'signInWithRedirect_failure') {
				broadcast({ type: 'SSO_ERROR', error: 'Authentication failed' });
				sessionStorage.removeItem(SSO_POPUP_FLAG);
				window.close();
			}
		});

		return unsubscribe;
	}, []);
};

const useParentSSOListener = (setError) => {
	useEffect(() => {
		const isPopupFlow = sessionStorage.getItem(SSO_POPUP_FLAG);
		if (isPopupFlow) return;

		const channel = new BroadcastChannel(AUTH_CHANNEL_NAME);

		channel.onmessage = (event) => {
			const { type, token, error } = event.data || {};

			if (type === 'SSO_SUCCESS' && token) {
				if (window.opener && !window.opener.closed) {
					window.opener.postMessage(token, '*');
				}
				channel.close();
				window.close();
			} else if (type === 'SSO_ERROR') {
				setError(error || 'Authentication failed');
				channel.close();
			}
		};

		return () => channel.close();
	}, [setError]);
};

const SalesforceLogin = () => {
	const theme = useTheme();
	const { authConfig, ready } = useAuthConfig();
	const [error, setError] = useState(null);

	const logo = theme.palette.mode === 'dark' ? logoWhite : logoBlack;
	const ssoProviders = authConfig?.oauth?.providers || [];
	const hasOAuthProviders = ssoProviders.length > 0;

	usePopupSSOCallback();
	useParentSSOListener(setError);

	const handleSSOLogin = (provider) => {
		setError(null);

		const sfUserId = sessionStorage.getItem('sfUserId');
		const ssoUrl = new URL(`/sf/sso/${provider}`, window.location.origin);
		if (sfUserId) ssoUrl.searchParams.set('id', sfUserId);

		const left = window.screen.width / 2 - 250;
		const top = window.screen.height / 2 - 300;
		const popup = window.open(
			ssoUrl,
			'sso-popup',
			`width=500,height=600,top=${top},left=${left}`
		);

		if (!popup)
			setError('Popup was blocked. Please allow popups for this site.');
	};

	if (!ready) return null;

	if (!authConfig) {
		return <NoAccountView logo={logo} />;
	}

	const authenticatorComponents = hasOAuthProviders
		? {
				SignIn: {
					Header: () => (
						<SSOSignInHeader
							providers={ssoProviders}
							onLogin={handleSSOLogin}
							error={error}
							onClearError={() => setError(null)}
						/>
					),
				},
			}
		: {};

	return (
		<div style={STYLES.centered}>
			<Authenticator hideSignUp components={authenticatorComponents}>
				<TokenReporter />
			</Authenticator>
		</div>
	);
};

export default SalesforceLogin;
